import React, { useState, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import apiClient from '../utils/apiClient';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  TicketIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowDownTrayIcon,
  BellIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  XMarkIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// === Story-Hero-Card Constants ===
const CARD_BG = '#1c2432';

interface Termin {
  id: number;
  veranstaltung_id: number;
  kalender_id: number | null;
  bezeichnung: string | null;
  datum: string;
  start_zeit: string;
  end_zeit: string;
  max_teilnehmer: number;
  buchungen_count: number;
}

interface Veranstaltung {
  id: number;
  titel: string;
  beschreibung: string | null;
  preis: number;
  buchen_bis: string;
  bezahlen_bis: string;
  aktiv: number;
  erstellt_von: number;
  erstellt_von_name: string;
  organisator_id: number | null;
  organisator_name: string | null;
  erstellt_am: string;
  buchungen_gesamt: number;
  bezahlt_count: number;
  offen_count: number;
  termine: Termin[];
}

interface Teilnehmer {
  buchung_id: number;
  gebucht_am: string;
  bezahlt_am: string | null;
  bezahlt_bei: string | null;
  mg_nr: number;
  member_name: string;
  email: string;
  termin_bezeichnung: string | null;
  termin_datum: string;
  termin_start: string;
  termin_end: string;
  bezahlen_bis: string;
}

interface NewTermin {
  bezeichnung: string;
  datum: string;
  start_zeit: string;
  end_zeit: string;
  max_teilnehmer: number;
}

interface MemberOption {
  mg_nr: number;
  name: string;
}

const EventManagement: React.FC = () => {
  const [events, setEvents] = useState<Veranstaltung[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const [participants, setParticipants] = useState<Teilnehmer[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [members, setMembers] = useState<MemberOption[]>([]);

  // Create/Edit Modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Veranstaltung | null>(null);
  const [formData, setFormData] = useState({
    titel: '',
    beschreibung: '',
    preis: 0,
    buchen_bis: '',
    bezahlen_bis: '',
    organisator: null as number | null,
  });
  const [formTermine, setFormTermine] = useState<NewTermin[]>([]);

  // Mark Paid Modal
  const [markPaidModal, setMarkPaidModal] = useState<{ buchungId: number; memberName: string } | null>(null);
  const [markPaidDate, setMarkPaidDate] = useState(new Date().toISOString().split('T')[0]);

  // Sort
  const [teilnehmerSort, setTeilnehmerSort] = useState<{ field: 'name' | 'status' | 'datum'; dir: 'asc' | 'desc' }>({ field: 'name', dir: 'asc' });

  // Confirmation Modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const fetchEvents = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/veranstaltungen/admin/alle');
      if (res.data.success) {
        setEvents(res.data.events);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Veranstaltungen:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Mitglieder für Organisator-Auswahl laden
  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/members');
      if (res.data) {
        const memberList = (Array.isArray(res.data) ? res.data : res.data.members || [])
          .filter((m: any) => !m.geloescht)
          .map((m: any) => ({
            mg_nr: m.mg_nr,
            name: `${m.vorname || ''} ${m.name || ''}`.trim(),
          }))
          .sort((a: MemberOption, b: MemberOption) => a.name.localeCompare(b.name, 'de'));
        setMembers(memberList);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Mitglieder:', err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchMembers();
  }, [fetchEvents, fetchMembers]);

  const fetchParticipants = async (eventId: number) => {
    setParticipantsLoading(true);
    try {
      const res = await apiClient.get(`/api/veranstaltungen/admin/${eventId}/teilnehmer`);
      if (res.data.success) {
        setParticipants(res.data.participants);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teilnehmer:', err);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const toggleExpand = (eventId: number) => {
    if (expandedEvent === eventId) {
      setExpandedEvent(null);
      setParticipants([]);
    } else {
      setExpandedEvent(eventId);
      fetchParticipants(eventId);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const isDeadlinePassed = (deadline: string) => {
    const today = new Date().toISOString().split('T')[0];
    return today > deadline;
  };

  const toggleSort = (field: 'name' | 'status' | 'datum') => {
    setTeilnehmerSort((prev) =>
      prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }
    );
  };

  const sortTeilnehmer = (list: Teilnehmer[]) => {
    const { field, dir } = teilnehmerSort;
    const sorted = [...list].sort((a, b) => {
      if (field === 'name') return a.member_name.localeCompare(b.member_name, 'de');
      if (field === 'status') {
        const statusVal = (t: Teilnehmer) => t.bezahlt_am ? 2 : (t.bezahlen_bis && isDeadlinePassed(t.bezahlen_bis) ? 0 : 1);
        return statusVal(a) - statusVal(b);
      }
      if (field === 'datum') return a.gebucht_am.localeCompare(b.gebucht_am);
      return 0;
    });
    return dir === 'desc' ? sorted.reverse() : sorted;
  };

  const groupByTermin = (list: Teilnehmer[]) => {
    const groups: Record<string, { label: string; datum: string; zeit: string; teilnehmer: Teilnehmer[] }> = {};
    for (const t of list) {
      const key = `${t.termin_datum}_${t.termin_start}`;
      if (!groups[key]) {
        groups[key] = { label: t.termin_bezeichnung || '', datum: t.termin_datum, zeit: `${t.termin_start}-${t.termin_end}`, teilnehmer: [] };
      }
      groups[key].teilnehmer.push(t);
    }
    return Object.values(groups).sort((a, b) => a.datum.localeCompare(b.datum) || a.zeit.localeCompare(b.zeit));
  };

  // ============ CREATE / EDIT ============

  const openCreateModal = () => {
    setEditingEvent(null);
    setFormData({ titel: '', beschreibung: '', preis: 0, buchen_bis: '', bezahlen_bis: '', organisator: null });
    setFormTermine([{ bezeichnung: '', datum: '', start_zeit: '10:00', end_zeit: '12:00', max_teilnehmer: 20 }]);
    setShowCreateModal(true);
  };

  const openEditModal = (event: Veranstaltung) => {
    setEditingEvent(event);
    setFormData({
      titel: event.titel,
      beschreibung: event.beschreibung || '',
      preis: event.preis,
      buchen_bis: event.buchen_bis ? event.buchen_bis.split('T')[0] : '',
      bezahlen_bis: event.bezahlen_bis ? event.bezahlen_bis.split('T')[0] : '',
      organisator: event.organisator_id || null,
    });
    setFormTermine([]);
    setShowCreateModal(true);
  };

  const addTerminRow = () => {
    setFormTermine((prev) => [...prev, { bezeichnung: '', datum: '', start_zeit: '10:00', end_zeit: '12:00', max_teilnehmer: 20 }]);
  };

  const removeTerminRow = (index: number) => {
    setFormTermine((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTerminRow = (index: number, field: keyof NewTermin, value: string | number) => {
    setFormTermine((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };

  const handleSave = async () => {
    if (!formData.titel || !formData.buchen_bis || !formData.bezahlen_bis) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Bitte Titel, Buchungsfrist und Zahlungsfrist ausfüllen.',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
      return;
    }

    try {
      if (editingEvent) {
        await apiClient.put(`/api/veranstaltungen/admin/${editingEvent.id}`, formData);
      } else {
        const validTermine = formTermine.filter((t) => t.datum && t.start_zeit && t.end_zeit);
        await apiClient.post('/api/veranstaltungen/admin', {
          ...formData,
          termine: validTermine,
        });
      }

      setShowCreateModal(false);
      await fetchEvents();

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: editingEvent ? 'Aktualisiert' : 'Erstellt',
        message: editingEvent ? 'Veranstaltung wurde aktualisiert.' : 'Veranstaltung wurde erstellt.',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Fehler beim Speichern';
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: msg,
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    }
  };

  // ============ DELETE EVENT ============

  const handleDeleteEvent = (event: Veranstaltung) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Veranstaltung löschen',
      message: `"${event.titel}" wirklich löschen?${event.buchungen_gesamt > 0 ? ' Es gibt aktive Buchungen - die Veranstaltung wird deaktiviert.' : ''}`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.delete(`/api/veranstaltungen/admin/${event.id}`);
          await fetchEvents();
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler beim Löschen',
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  // ============ TOGGLE ACTIVE ============

  const handleToggleActive = async (event: Veranstaltung) => {
    try {
      await apiClient.put(`/api/veranstaltungen/admin/${event.id}`, { aktiv: event.aktiv ? 0 : 1 });
      await fetchEvents();
    } catch (err) {
      console.error('Fehler beim Umschalten:', err);
    }
  };

  // ============ MARK PAID ============

  const handleMarkPaid = async () => {
    if (!markPaidModal) return;

    try {
      await apiClient.post(`/api/veranstaltungen/admin/buchungen/${markPaidModal.buchungId}/mark-paid`, {
        bezahlt_am: markPaidDate,
        bezahlt_bei: 'Vorstand',
      });

      setMarkPaidModal(null);

      if (expandedEvent) {
        await fetchParticipants(expandedEvent);
      }
      await fetchEvents();

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Bezahlt',
        message: `Zahlung für ${markPaidModal.memberName} markiert.`,
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Markieren',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    }
  };

  // ============ EXPORT ============

  const handleExport = async (eventId: number) => {
    try {
      const res = await apiClient.get(`/api/veranstaltungen/admin/${eventId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const event = events.find((e) => e.id === eventId);
      link.download = `Teilnehmer_${event?.titel || 'Export'}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export-Fehler:', err);
    }
  };

  const handleExportPDF = (eventId: number) => {
    const event = events.find((e) => e.id === eventId);
    if (!event || participants.length === 0) return;

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    // Teilnehmer nach Termin-Datum gruppieren
    const grouped: { [key: string]: { label: string; participants: Teilnehmer[] } } = {};
    participants.forEach((p) => {
      const key = `${p.termin_datum}_${p.termin_start}`;
      if (!grouped[key]) {
        const datum = new Date(p.termin_datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
        const zeit = `${p.termin_start?.slice(0, 5) || ''} – ${p.termin_end?.slice(0, 5) || ''} Uhr`;
        grouped[key] = {
          label: `${datum}  ·  ${zeit}${p.termin_bezeichnung ? `  ·  ${p.termin_bezeichnung}` : ''}`,
          participants: []
        };
      }
      grouped[key].participants.push(p);
    });

    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    let isFirstPage = true;

    sortedGroups.forEach(([, group]) => {
      if (!isFirstPage) doc.addPage();
      isFirstPage = false;

      let y = margin;

      // Vereinsname
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140);
      doc.text('HSV Berlin-Adlershof e.V.', margin, y + 4);
      const orgName = event.organisator_name || event.erstellt_von_name;
      if (orgName) {
        doc.text(`Organisator: ${orgName}`, pageWidth - margin, y + 4, { align: 'right' });
      }
      y += 8;

      // Veranstaltungs-Header
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(34, 120, 74);
      doc.text(event.titel, margin, y + 6);
      y += 10;

      // Termin-Info
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60);
      doc.text(group.label, margin, y + 5);
      y += 8;

      // Info-Zeile
      doc.setFontSize(9);
      doc.setTextColor(120);
      const preis = Number(event.preis || 0);
      const info = preis > 0
        ? `${group.participants.length} Teilnehmer  ·  Preis: ${preis.toFixed(2)} €  ·  Bezahlt: ${group.participants.filter(p => p.bezahlt_am).length}  ·  Offen: ${group.participants.filter(p => !p.bezahlt_am).length}`
        : `${group.participants.length} Teilnehmer`;
      doc.text(info, margin, y + 4);
      y += 8;

      // Trennlinie
      doc.setDrawColor(34, 120, 74);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 4;

      // Teilnehmer-Tabelle als Anwesenheitsliste
      const headers = [['#', 'Name', 'Bezahlt', 'Anw.', 'Unterschrift']];
      const rows = group.participants
        .sort((a, b) => a.member_name.localeCompare(b.member_name))
        .map((p, i) => [
          String(i + 1),
          p.member_name,
          p.bezahlt_am ? 'Ja' : '—',
          '',  // Anwesenheit (Checkbox-Platz)
          '',  // Unterschrift (Leerzeile)
        ]);

      autoTable(doc, {
        head: headers,
        body: rows,
        startY: y,
        margin: { left: margin, right: margin },
        styles: {
          fontSize: 10,
          cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
          lineColor: [220, 220, 220],
          lineWidth: 0.3,
        },
        headStyles: {
          fillColor: [34, 120, 74],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        },
        bodyStyles: {
          textColor: [40, 40, 40],
        },
        alternateRowStyles: { fillColor: [248, 250, 248] },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' as const, textColor: [150, 150, 150], fontSize: 8 },
          1: { cellWidth: 55, fontStyle: 'bold' as const },
          2: { cellWidth: 22, halign: 'center' as const },
          3: { cellWidth: 16, halign: 'center' as const },   // Checkbox
          4: { cellWidth: 'auto' as const, minCellWidth: 60 },  // Unterschrift
        },
        didParseCell: (data: any) => {
          // Bezahlt-Spalte farbig
          if (data.section === 'body' && data.column.index === 2) {
            if (data.cell.raw === 'Ja') {
              data.cell.styles.textColor = [34, 120, 74];
            } else {
              data.cell.styles.textColor = [200, 80, 60];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        },
        didDrawCell: (data: any) => {
          // Checkbox zeichnen (Anwesenheit)
          if (data.section === 'body' && data.column.index === 3) {
            const size = 4;
            const cx = data.cell.x + (data.cell.width - size) / 2;
            const cy = data.cell.y + (data.cell.height - size) / 2;
            doc.setDrawColor(160, 160, 160);
            doc.setLineWidth(0.4);
            doc.roundedRect(cx, cy, size, size, 0.8, 0.8, 'S');
          }
          // Unterschriftslinie zeichnen
          if (data.section === 'body' && data.column.index === 4) {
            const lineY = data.cell.y + data.cell.height - 4;
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(data.cell.x + 4, lineY, data.cell.x + data.cell.width - 4, lineY);
          }
        },
      } as any);
    });

    // Footer auf allen Seiten
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(170);
      doc.text(
        `${event.titel}  ·  Erstellt am ${new Date().toLocaleDateString('de-DE')}  ·  Seite ${i}/${pageCount}`,
        pageWidth - margin, pageHeight - 8,
        { align: 'right' }
      );
    }

    doc.save(`Anwesenheit_${event.titel.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}.pdf`);
  };

  // ============ NOTIFY UNPAID ============

  const handleNotifyUnpaid = (eventId: number) => {
    const event = events.find((e) => e.id === eventId);
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: 'Zahlungserinnerung',
      message: `Zahlungserinnerung an alle ${event?.offen_count || 0} unbezahlten Teilnehmer von "${event?.titel}" senden?`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          const res = await apiClient.post(`/api/veranstaltungen/admin/${eventId}/notify-unpaid`);
          setConfirmModal({
            isOpen: true,
            type: 'success',
            title: 'Gesendet',
            message: `${res.data.count} Erinnerung(en) gesendet.`,
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler beim Senden',
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  // ============ ADD TERMIN TO EXISTING EVENT ============

  const [addTerminEventId, setAddTerminEventId] = useState<number | null>(null);
  const [newTermin, setNewTermin] = useState<NewTermin>({ bezeichnung: '', datum: '', start_zeit: '10:00', end_zeit: '12:00', max_teilnehmer: 20 });

  const handleAddTermin = async () => {
    if (!addTerminEventId || !newTermin.datum || !newTermin.start_zeit || !newTermin.end_zeit) return;

    try {
      await apiClient.post(`/api/veranstaltungen/admin/${addTerminEventId}/termine`, newTermin);
      setAddTerminEventId(null);
      setNewTermin({ bezeichnung: '', datum: '', start_zeit: '10:00', end_zeit: '12:00', max_teilnehmer: 20 });
      await fetchEvents();
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Hinzufügen',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    }
  };

  // ============ DELETE TERMIN ============

  const handleDeleteTermin = (terminId: number) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Termin löschen',
      message: 'Termin wirklich löschen?',
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.delete(`/api/veranstaltungen/admin/termine/${terminId}`);
          await fetchEvents();
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler beim Löschen',
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  // ============ ADD MEMBER TO EVENT ============

  const [addMemberModal, setAddMemberModal] = useState<{ eventId: number; termine: Termin[] } | null>(null);
  const [addMemberForm, setAddMemberForm] = useState({ member_id: 0, termin_id: 0, gast_name: '', gast_email: '' });
  const [addMemberIsGuest, setAddMemberIsGuest] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [memberHighlight, setMemberHighlight] = useState(-1);
  const memberSearchRef = React.useRef<HTMLInputElement>(null);
  const memberListRef = React.useRef<HTMLDivElement>(null);

  // Scroll highlighted member into view
  useEffect(() => {
    if (memberListRef.current && memberHighlight >= 0) {
      const items = memberListRef.current.querySelectorAll('button');
      if (items[memberHighlight]) {
        items[memberHighlight].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [memberHighlight]);

  const handleAddMember = async () => {
    if (!addMemberModal || !addMemberForm.termin_id) return;
    if (!addMemberIsGuest && !addMemberForm.member_id) return;
    if (addMemberIsGuest && !addMemberForm.gast_name.trim()) return;

    try {
      const payload: any = { termin_id: addMemberForm.termin_id };
      if (addMemberIsGuest) {
        payload.gast_name = addMemberForm.gast_name.trim();
        payload.gast_email = addMemberForm.gast_email.trim() || undefined;
      } else {
        payload.member_id = addMemberForm.member_id;
      }

      await apiClient.post(`/api/veranstaltungen/admin/${addMemberModal.eventId}/buchen-fuer`, payload);

      setAddMemberModal(null);
      setAddMemberForm({ member_id: 0, termin_id: 0, gast_name: '', gast_email: '' });
      setAddMemberIsGuest(false);
      setMemberSearch('');
      setMemberDropdownOpen(false);

      if (expandedEvent) {
        await fetchParticipants(expandedEvent);
      }
      await fetchEvents();

      setConfirmModal({
        isOpen: true,
        type: 'success',
        title: 'Hinzugefügt',
        message: addMemberIsGuest ? 'Gast wurde erfolgreich gebucht.' : 'Mitglied wurde erfolgreich gebucht.',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Hinzufügen',
        variant: 'single',
        onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
        onCancel: () => {},
      });
    }
  };

  // ============ REMOVE BOOKING ============

  const handleRemoveBooking = (buchungId: number, memberName: string) => {
    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: 'Buchung entfernen',
      message: `Buchung von "${memberName}" wirklich entfernen? Das Mitglied wird per E-Mail benachrichtigt.`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.delete(`/api/veranstaltungen/admin/buchungen/${buchungId}`);
          if (expandedEvent) {
            await fetchParticipants(expandedEvent);
          }
          await fetchEvents();
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler beim Entfernen',
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  // ============ UNMARK PAID ============

  const handleUnmarkPaid = (buchungId: number, memberName: string) => {
    setConfirmModal({
      isOpen: true,
      type: 'question',
      title: 'Bezahlt-Status entfernen',
      message: `Bezahlt-Status von "${memberName}" wirklich entfernen? Das Mitglied wird per E-Mail benachrichtigt.`,
      variant: 'two',
      onConfirm: async () => {
        setConfirmModal((p) => ({ ...p, isOpen: false }));
        try {
          await apiClient.post(`/api/veranstaltungen/admin/buchungen/${buchungId}/unmark-paid`);
          if (expandedEvent) {
            await fetchParticipants(expandedEvent);
          }
          await fetchEvents();
        } catch (err: any) {
          setConfirmModal({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: err.response?.data?.message || 'Fehler beim Entfernen des Status',
            variant: 'single',
            onConfirm: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
            onCancel: () => {},
          });
        }
      },
      onCancel: () => setConfirmModal((p) => ({ ...p, isOpen: false })),
    });
  };

  // Filter events by search
  const filteredEvents = events.filter((e) =>
    !searchTerm || e.titel.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const totalBookings = events.reduce((sum, e) => sum + e.buchungen_gesamt, 0);
  const totalPaid = events.reduce((sum, e) => sum + e.bezahlt_count, 0);
  const totalOpen = events.reduce((sum, e) => sum + e.offen_count, 0);
  const totalRevenue = events.reduce((sum, e) => sum + e.preis * e.bezahlt_count, 0);

  if (loading) {
    return (
      <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto py-2 sm:py-4 flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
        </div>
      </div>
    );
  }

  const monthYear = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const upcomingEvents = events.filter(e => e.aktiv).length;

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4 space-y-4 lg:space-y-6">

        {/* INTRO Schriftzug */}
        <section className="pt-2 pb-2">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            Verwaltung · Stand {monthYear}
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
            Termin-<br />
            <span className="text-amber-600 dark:text-amber-400">Zentrale.</span>
          </h1>
          <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
            {events.length === 0 ? (
              <>Noch keine Veranstaltungen — leg deine <span className="font-bold text-gray-900 dark:text-white">erste Buchungs-Veranstaltung</span> an.</>
            ) : (
              <>
                <span className="font-bold text-gray-900 dark:text-white">{events.length} {events.length === 1 ? 'Veranstaltung' : 'Veranstaltungen'}</span>
                {upcomingEvents > 0 && <> · <span className="font-bold text-emerald-700 dark:text-emerald-300">{upcomingEvents} aktiv</span></>}
                {totalBookings > 0 && <> · <span className="font-bold text-gray-900 dark:text-white">{totalBookings} Buchungen</span></>}
                {totalOpen > 0 && <> · <span className="font-bold text-amber-700 dark:text-amber-300">{totalOpen} offene Zahlungen</span></>}
                .
              </>
            )}
          </p>
        </section>

        {/* Toolbar + Action */}
        <div className="rounded-3xl p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="relative flex-1 min-w-0 w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Veranstaltung suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-11 pl-9 pr-4 rounded-xl text-sm text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
              />
            </div>
            <button
              onClick={openCreateModal}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm transition whitespace-nowrap"
            >
              <PlusIcon className="h-4 w-4" />
              Neue Veranstaltung
            </button>
          </div>
        </div>

      {/* Events List */}
      <div className="space-y-3">
        {filteredEvents.map((event) => {
          const isExpanded = expandedEvent === event.id;

          return (
            <div key={event.id} className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              {/* Event Row */}
              <div
                className="p-4 sm:p-5 cursor-pointer transition-colors"
                onClick={() => toggleExpand(event.id)}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.025)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${event.aktiv ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{event.titel}</h3>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600 dark:text-slate-400 flex-wrap">
                        <span className="tabular-nums">{formatCurrency(event.preis)}</span>
                        <span>{event.termine.length} Termine</span>
                        <span>
                          <UserGroupIcon className="h-3.5 w-3.5 inline mr-0.5" />
                          {event.buchungen_gesamt}
                        </span>
                        {event.offen_count > 0 && (
                          <span className="text-amber-700 dark:text-amber-300">{event.offen_count} offen</span>
                        )}
                        {event.organisator_name && (
                          <span className="text-cyan-300">Org: {event.organisator_name}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
                      className="p-1.5 text-gray-600 dark:text-slate-400 hover:text-white transition-colors"
                      title="Bearbeiten"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleActive(event); }}
                      className={`p-1.5 transition-colors ${event.aktiv ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-500 hover:text-slate-300'}`}
                      title={event.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {event.aktiv ? <CheckCircleIcon className="h-4 w-4" /> : <XCircleIcon className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event); }}
                      className="p-1.5 text-gray-600 dark:text-slate-400 hover:text-rose-400 transition-colors"
                      title="Löschen"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-600 dark:text-slate-400" /> : <ChevronDownIcon className="h-4 w-4 text-gray-600 dark:text-slate-400" />}
                  </div>
                </div>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid #1e293b' }}>
                  {/* Event Info */}
                  <div className="p-4 sm:p-5" style={{ background: 'rgba(255,255,255,0.015)' }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Preis:</span>{' '}
                        <span className="font-medium text-gray-900 dark:text-white tabular-nums">{formatCurrency(event.preis)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Buchen bis:</span>{' '}
                        <span className={`font-medium ${isDeadlinePassed(event.buchen_bis) ? 'text-rose-400' : 'text-white'}`}>
                          {formatDate(event.buchen_bis)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Bezahlen bis:</span>{' '}
                        <span className={`font-medium ${isDeadlinePassed(event.bezahlen_bis) ? 'text-rose-400' : 'text-white'}`}>
                          {formatDate(event.bezahlen_bis)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 dark:text-slate-500">Erstellt von:</span>{' '}
                        <span className="font-medium text-gray-900 dark:text-white">{event.erstellt_von_name}</span>
                      </div>
                      {event.organisator_name && (
                        <div>
                          <span className="text-gray-500 dark:text-slate-500">Organisator:</span>{' '}
                          <span className="font-medium text-cyan-300">{event.organisator_name}</span>
                        </div>
                      )}
                    </div>
                    {event.beschreibung && (
                      <p className="mt-2 text-sm text-gray-700 dark:text-slate-300">{event.beschreibung}</p>
                    )}
                  </div>

                  {/* Termine */}
                  <div className="p-4 sm:p-5" style={{ borderTop: '1px solid #1e293b' }}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Termine</h4>
                      <button
                        onClick={() => setAddTerminEventId(addTerminEventId === event.id ? null : event.id)}
                        className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-200 font-medium flex items-center gap-1"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        Termin hinzufügen
                      </button>
                    </div>

                    {/* Add Termin Form */}
                    {addTerminEventId === event.id && (
                      <div className="mb-3 p-3 rounded-xl bg-gray-50 dark:bg-slate-900/60" style={{ border: '1px dashed #334155'  }}>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          <input
                            type="text"
                            placeholder="Bezeichnung"
                            value={newTermin.bezeichnung}
                            onChange={(e) => setNewTermin((p) => ({ ...p, bezeichnung: e.target.value }))}
                            className="col-span-2 sm:col-span-1 px-3 py-1.5 rounded-lg text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                            style={{ colorScheme: 'dark'  }}
                          />
                          <input
                            type="date"
                            value={newTermin.datum}
                            onChange={(e) => setNewTermin((p) => ({ ...p, datum: e.target.value }))}
                            className="px-3 py-1.5 rounded-lg text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                            style={{ colorScheme: 'dark'  }}
                          />
                          <input
                            type="time"
                            value={newTermin.start_zeit}
                            onChange={(e) => setNewTermin((p) => ({ ...p, start_zeit: e.target.value }))}
                            className="px-3 py-1.5 rounded-lg text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                            style={{ colorScheme: 'dark'  }}
                          />
                          <input
                            type="time"
                            value={newTermin.end_zeit}
                            onChange={(e) => setNewTermin((p) => ({ ...p, end_zeit: e.target.value }))}
                            className="px-3 py-1.5 rounded-lg text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                            style={{ colorScheme: 'dark'  }}
                          />
                          <div className="flex gap-2">
                            <input
                              type="number"
                              placeholder="Max"
                              value={newTermin.max_teilnehmer}
                              onChange={(e) => setNewTermin((p) => ({ ...p, max_teilnehmer: parseInt(e.target.value) || 20 }))}
                              className="w-20 px-3 py-1.5 rounded-lg text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                              style={{ colorScheme: 'dark'  }}
                            />
                            <button
                              onClick={handleAddTermin}
                              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-bold"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      {event.termine.map((termin) => (
                        <div key={termin.id} className="flex items-center justify-between p-2.5 rounded-lg text-sm border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.4)' }}>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-medium text-gray-900 dark:text-white">{formatDate(termin.datum)}</span>
                            <span className="text-gray-600 dark:text-slate-400">
                              <ClockIcon className="h-3.5 w-3.5 inline mr-0.5" />
                              {termin.start_zeit?.substring(0, 5)}-{termin.end_zeit?.substring(0, 5)}
                            </span>
                            {termin.bezeichnung && <span className="text-xs text-gray-500 dark:text-slate-500">({termin.bezeichnung})</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 dark:text-slate-400 tabular-nums">
                              <UserGroupIcon className="h-3.5 w-3.5 inline mr-0.5" />
                              {termin.buchungen_count}/{termin.max_teilnehmer}
                            </span>
                            {termin.buchungen_count === 0 && (
                              <button
                                onClick={() => handleDeleteTermin(termin.id)}
                                className="p-1 text-gray-600 dark:text-slate-400 hover:text-rose-400"
                                title="Termin löschen"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Teilnehmer */}
                  <div className="p-4 sm:p-5" style={{ borderTop: '1px solid #1e293b' }}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        Teilnehmer ({participants.length})
                      </h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setAddMemberModal({ eventId: event.id, termine: event.termine })}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-amber-700 dark:text-amber-300 rounded-lg transition-colors"
                          style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                        >
                          <UserPlusIcon className="h-3.5 w-3.5" />
                          Mitglied hinzufügen
                        </button>
                        {event.offen_count > 0 && event.preis > 0 && (
                          <button
                            onClick={() => handleNotifyUnpaid(event.id)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-amber-700 dark:text-amber-300 rounded-lg transition-colors"
                            style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                          >
                            <BellIcon className="h-3.5 w-3.5" />
                            Alle erinnern
                          </button>
                        )}
                        <button
                          onClick={() => handleExport(event.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-300 rounded-lg transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                          CSV
                        </button>
                        <button
                          onClick={() => handleExportPDF(event.id)}
                          disabled={participants.length === 0}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-300 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          <DocumentTextIcon className="h-3.5 w-3.5" />
                          PDF
                        </button>
                      </div>
                    </div>

                    {participantsLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-400"></div>
                      </div>
                    ) : participants.length === 0 ? (
                      <p className="text-sm text-gray-600 dark:text-slate-400 text-center py-4">Keine Teilnehmer</p>
                    ) : (() => {
                      const groups = groupByTermin(participants);
                      const hasMultipleTermine = groups.length > 1;
                      const SortIcon = ({ field }: { field: 'name' | 'status' | 'datum' }) => (
                        teilnehmerSort.field === field
                          ? (teilnehmerSort.dir === 'asc' ? <ChevronUpIcon className="h-3 w-3 inline ml-0.5" /> : <ChevronDownIcon className="h-3 w-3 inline ml-0.5" />)
                          : <span className="inline-block w-3 h-3 ml-0.5" />
                      );

                      return (
                        <div className="space-y-4">
                          {groups.map((group, gi) => (
                            <div key={gi}>
                              {hasMultipleTermine && (
                                <div className="flex items-center gap-2 mb-2">
                                  <CalendarDaysIcon className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatDate(group.datum)} {group.zeit}
                                  </span>
                                  {group.label && <span className="text-xs text-gray-600 dark:text-slate-400">({group.label})</span>}
                                  <span className="text-xs text-gray-500 dark:text-slate-500">({group.teilnehmer.length})</span>
                                </div>
                              )}
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm table-fixed">
                                  <thead>
                                    <tr className="text-left text-xs text-gray-600 dark:text-slate-400" style={{ borderBottom: '1px solid #1e293b' }}>
                                      <th className="pb-2 font-medium cursor-pointer select-none hover:text-white w-[30%]" onClick={() => toggleSort('name')}>
                                        Name <SortIcon field="name" />
                                      </th>
                                      <th className="pb-2 font-medium hidden sm:table-cell w-[25%]">Email</th>
                                      {event.preis > 0 && (
                                        <th className="pb-2 font-medium cursor-pointer select-none hover:text-white w-[12%]" onClick={() => toggleSort('status')}>
                                          Status <SortIcon field="status" />
                                        </th>
                                      )}
                                      <th className="pb-2 font-medium hidden sm:table-cell cursor-pointer select-none hover:text-white w-[18%]" onClick={() => toggleSort('datum')}>
                                        Gebucht <SortIcon field="datum" />
                                      </th>
                                      <th className="pb-2 font-medium text-right w-[15%]">Aktion</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortTeilnehmer(group.teilnehmer).map((p) => {
                                      const isPaid = !!p.bezahlt_am;
                                      const isOverdue = !isPaid && isDeadlinePassed(p.bezahlen_bis);

                                      return (
                                        <tr key={p.buchung_id} style={{ borderBottom: '1px solid #1e293b' }}>
                                          <td className="py-2.5 font-medium text-gray-900 dark:text-white truncate">{p.member_name}</td>
                                          <td className="py-2.5 text-gray-600 dark:text-slate-400 hidden sm:table-cell truncate">{p.email}</td>
                                          {event.preis > 0 && (
                                            <td className="py-2.5">
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                                                isPaid
                                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                                                  : isOverdue
                                                    ? 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                                                    : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                                              }`}>
                                                {isPaid ? 'Bezahlt' : isOverdue ? 'Überfällig' : 'Offen'}
                                              </span>
                                            </td>
                                          )}
                                          <td className="py-2.5 text-gray-600 dark:text-slate-400 hidden sm:table-cell text-xs">{formatDateTime(p.gebucht_am)}</td>
                                          <td className="py-2.5 text-right">
                                            <div className="flex items-center gap-2 justify-end">
                                              {!isPaid && event.preis > 0 && (
                                                <button
                                                  onClick={() => setMarkPaidModal({ buchungId: p.buchung_id, memberName: p.member_name })}
                                                  className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-200 font-medium flex items-center gap-1"
                                                >
                                                  <BanknotesIcon className="h-3.5 w-3.5" />
                                                  Bezahlt
                                                </button>
                                              )}
                                              {isPaid && event.preis > 0 && (
                                                <button
                                                  onClick={() => handleUnmarkPaid(p.buchung_id, p.member_name)}
                                                  className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-200 font-medium flex items-center gap-1"
                                                  title="Bezahlt-Status entfernen"
                                                >
                                                  <XMarkIcon className="h-3.5 w-3.5" />
                                                </button>
                                              )}
                                              <button
                                                onClick={() => handleRemoveBooking(p.buchung_id, p.member_name)}
                                                className="text-xs text-rose-600 dark:text-rose-400 hover:text-rose-300 font-medium flex items-center gap-1"
                                                title="Buchung entfernen"
                                              >
                                                <TrashIcon className="h-3.5 w-3.5" />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {hasMultipleTermine && (
                                  <div className="mt-1 text-xs text-gray-500 dark:text-slate-500">
                                    {group.teilnehmer.length} Teilnehmer
                                    {event.preis > 0 && <> | Bezahlt: {group.teilnehmer.filter(t => t.bezahlt_am).length}</>}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <div className="text-xs text-gray-600 dark:text-slate-400 pt-2" style={{ borderTop: '1px solid #1e293b' }}>
                            Gesamt: {participants.length} Teilnehmer
                            {event.preis > 0 && <> | Bezahlt: {participants.filter(t => t.bezahlt_am).length}</>}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredEvents.length === 0 && (
          <div className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
            <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
              <TicketIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h4 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Keine Veranstaltungen</h4>
            <p className="text-gray-600 dark:text-slate-400 text-sm mt-2">Lege deine erste Veranstaltung an, um Mitglieder einzuladen.</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowCreateModal(false)} />
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white dark:bg-[#1c2432] w-full sm:max-w-lg sm:rounded-xl rounded-t-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {editingEvent ? 'Veranstaltung bearbeiten' : 'Neue Veranstaltung'}
                </h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titel *</label>
                  <input
                    type="text"
                    value={formData.titel}
                    onChange={(e) => setFormData((p) => ({ ...p, titel: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    placeholder="z.B. Sommerfest 2026"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Beschreibung</label>
                  <textarea
                    value={formData.beschreibung}
                    onChange={(e) => setFormData((p) => ({ ...p, beschreibung: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none resize-none"
                    placeholder="Beschreibung der Veranstaltung..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preis (EUR)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.preis}
                    onChange={(e) => setFormData((p) => ({ ...p, preis: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Buchen bis *</label>
                    <input
                      type="date"
                      value={formData.buchen_bis}
                      onChange={(e) => setFormData((p) => ({ ...p, buchen_bis: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bezahlen bis *</label>
                    <input
                      type="date"
                      value={formData.bezahlen_bis}
                      onChange={(e) => setFormData((p) => ({ ...p, bezahlen_bis: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Organisator */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organisator (optional)</label>
                  <select
                    value={formData.organisator || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, organisator: e.target.value ? parseInt(e.target.value) : null }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="">Kein Organisator</option>
                    {members.map((m) => (
                      <option key={m.mg_nr} value={m.mg_nr}>{m.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Der Organisator erhält volle Einsicht in Teilnehmerlisten.</p>
                </div>

                {/* Termine (nur beim Erstellen) */}
                {!editingEvent && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Termine</label>
                      <button onClick={addTerminRow} className="text-xs text-primary-600 dark:text-primary-400 font-medium flex items-center gap-1">
                        <PlusIcon className="h-3.5 w-3.5" />
                        Termin
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formTermine.map((termin, idx) => (
                        <div key={idx} className="grid grid-cols-6 gap-2 items-end">
                          <input
                            type="text"
                            placeholder="Bezeichnung"
                            value={termin.bezeichnung}
                            onChange={(e) => updateTerminRow(idx, 'bezeichnung', e.target.value)}
                            className="col-span-6 sm:col-span-2 px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <input
                            type="date"
                            value={termin.datum}
                            onChange={(e) => updateTerminRow(idx, 'datum', e.target.value)}
                            className="col-span-2 sm:col-span-1 px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <input
                            type="time"
                            value={termin.start_zeit}
                            onChange={(e) => updateTerminRow(idx, 'start_zeit', e.target.value)}
                            className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <input
                            type="time"
                            value={termin.end_zeit}
                            onChange={(e) => updateTerminRow(idx, 'end_zeit', e.target.value)}
                            className="px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          />
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              placeholder="Max"
                              value={termin.max_teilnehmer}
                              onChange={(e) => updateTerminRow(idx, 'max_teilnehmer', parseInt(e.target.value) || 20)}
                              className="w-16 px-2 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                            {formTermine.length > 1 && (
                              <button onClick={() => removeTerminRow(idx)} className="p-1 text-gray-400 hover:text-error-600">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {editingEvent ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mark Paid Modal */}
      {markPaidModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setMarkPaidModal(null)} />
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white dark:bg-[#1c2432] w-full sm:max-w-sm sm:rounded-xl rounded-t-xl border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Zahlung markieren</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{markPaidModal.memberName}</p>
              </div>
              <div className="p-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bezahlt am</label>
                <input
                  type="date"
                  value={markPaidDate}
                  onChange={(e) => setMarkPaidDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                />
              </div>
              <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => setMarkPaidModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleMarkPaid}
                  className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white bg-primary-600 hover:bg-primary-700 rounded-lg"
                >
                  Als bezahlt markieren
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Member Modal */}
      {addMemberModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => { setAddMemberModal(null); setAddMemberIsGuest(false); setMemberSearch(''); setMemberDropdownOpen(false); }} />
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white dark:bg-[#1c2432] w-full sm:max-w-sm sm:rounded-xl rounded-t-xl border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Teilnehmer hinzufügen</h3>
              </div>
              <div className="p-5 space-y-4">
                {/* Toggle: Mitglied / Gast */}
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setAddMemberIsGuest(false); setAddMemberForm((p) => ({ ...p, gast_name: '', gast_email: '' })); }}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      !addMemberIsGuest
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    Mitglied
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddMemberIsGuest(true); setAddMemberForm((p) => ({ ...p, member_id: 0 })); setMemberSearch(''); setMemberDropdownOpen(false); }}
                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                      addMemberIsGuest
                        ? 'bg-primary-600 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    Gast
                  </button>
                </div>

                {!addMemberIsGuest ? (
                  /* Member search */
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mitglied</label>
                    <input
                      ref={memberSearchRef}
                      type="text"
                      placeholder="Name eingeben zum Suchen..."
                      value={memberSearch}
                      onChange={(e) => {
                        setMemberSearch(e.target.value);
                        setMemberDropdownOpen(true);
                        setMemberHighlight(0);
                        if (!e.target.value) {
                          setAddMemberForm((p) => ({ ...p, member_id: 0 }));
                        }
                      }}
                      onFocus={() => setMemberDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
                      onKeyDown={(e) => {
                        const filtered = members.filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase()));
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setMemberHighlight((prev) => Math.min(prev + 1, filtered.length - 1));
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setMemberHighlight((prev) => Math.max(prev - 1, 0));
                        } else if (e.key === 'Enter' && memberHighlight >= 0 && filtered[memberHighlight]) {
                          e.preventDefault();
                          setAddMemberForm((p) => ({ ...p, member_id: filtered[memberHighlight].mg_nr }));
                          setMemberSearch(filtered[memberHighlight].name);
                          setMemberDropdownOpen(false);
                        } else if (e.key === 'Escape') {
                          setMemberDropdownOpen(false);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    />
                    {memberDropdownOpen && memberSearch.length > 0 && (
                      <div
                        ref={memberListRef}
                        className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
                      >
                        {members
                          .filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase()))
                          .map((m, idx) => (
                            <button
                              key={m.mg_nr}
                              type="button"
                              onClick={() => {
                                setAddMemberForm((p) => ({ ...p, member_id: m.mg_nr }));
                                setMemberSearch(m.name);
                                setMemberDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                idx === memberHighlight
                                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                              } ${addMemberForm.member_id === m.mg_nr ? 'font-semibold' : ''}`}
                            >
                              {m.name}
                            </button>
                          ))}
                        {members.filter((m) => m.name.toLowerCase().includes(memberSearch.toLowerCase())).length === 0 && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Kein Mitglied gefunden</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Guest inputs */
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                      <input
                        type="text"
                        placeholder="Vor- und Nachname"
                        value={addMemberForm.gast_name}
                        onChange={(e) => setAddMemberForm((p) => ({ ...p, gast_name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-Mail (optional)</label>
                      <input
                        type="email"
                        placeholder="gast@example.de"
                        value={addMemberForm.gast_email}
                        onChange={(e) => setAddMemberForm((p) => ({ ...p, gast_email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termin</label>
                  <select
                    value={addMemberForm.termin_id || ''}
                    onChange={(e) => setAddMemberForm((p) => ({ ...p, termin_id: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  >
                    <option value="">Termin auswählen...</option>
                    {addMemberModal.termine.map((t) => (
                      <option key={t.id} value={t.id}>
                        {new Date(t.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}{' '}
                        {t.start_zeit?.substring(0, 5)}-{t.end_zeit?.substring(0, 5)}
                        {t.bezeichnung ? ` (${t.bezeichnung})` : ''}
                        {' '}[{t.buchungen_count}/{t.max_teilnehmer}]
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => { setAddMemberModal(null); setAddMemberIsGuest(false); setMemberSearch(''); setMemberDropdownOpen(false); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAddMember}
                  disabled={
                    !addMemberForm.termin_id ||
                    (!addMemberIsGuest && !addMemberForm.member_id) ||
                    (addMemberIsGuest && !addMemberForm.gast_name.trim())
                  }
                  className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hinzufügen
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onClose={() => setConfirmModal((p) => ({ ...p, isOpen: false }))}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: confirmModal.onCancel },
          confirm: { text: 'Bestätigen', onPress: confirmModal.onConfirm },
        } : undefined}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm,
        } : undefined}
      />
      </div>
    </div>
  );
};

export default EventManagement;
